import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { loginWithVerification, verifyCode } from '../services/api';
import { TextField, Button, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { motion } from 'framer-motion';
import { keyframes } from '@mui/system';

const _LOGIN_WITHOUT_EMAIL_ENABLED = false;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Root = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  background: 'var(--color-background)',
  padding: theme.spacing(3),
  transition: 'all 0.3s ease-in-out',
}));

const StyledPaper = styled(motion.div)(({ theme }) => ({
  padding: theme.spacing(6),
  maxWidth: 450,
  width: '100%',
  backgroundColor: 'var(--color-surface)',
  borderRadius: 24,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  animation: `${fadeIn} 0.6s ease-out`,
  position: 'relative',
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, var(--color-primary), var(--color-accent))',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
    margin: theme.spacing(2),
  }
}));

const LogoText = styled(motion.h1)(({ theme }) => ({
  fontSize: '2.8rem',
  fontWeight: 700,
  background: 'linear-gradient(45deg, var(--color-primary), var(--color-accent))',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
    marginBottom: theme.spacing(2),
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  color: 'var(--color-primary)',
  fontWeight: 600,
  textAlign: 'center',
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: 'red',
  marginBottom: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.3s ease',
    borderRadius: 12,
    '& fieldset': {
      borderColor: 'var(--color-border)',
      transition: 'all 0.3s ease',
    },
    '&:hover fieldset': {
      borderColor: 'var(--color-primary)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--color-accent)',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--color-text-secondary)',
    '&.Mui-focused': {
      color: 'var(--color-accent)',
    },
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

const LoginButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  fontWeight: 600,
  borderRadius: 30,
  textTransform: 'none',
  fontSize: '1rem',
  transition: 'all 0.3s ease',
  backgroundColor: 'var(--color-accent)',
  color: 'var(--color-primary)',
  '&:hover': {
    backgroundColor: 'var(--color-accent-light)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
}));

const _WarningText = styled(Typography)(({ theme }) => ({
  color: theme.palette.warning.main,
  marginTop: theme.spacing(2),
  textAlign: 'center',
  fontSize: '0.9rem',
}));

const QuoteBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  textAlign: 'center',
}));

const QuoteText = styled(Typography)(({ theme }) => ({
  fontStyle: 'italic',
  marginBottom: theme.spacing(1),
}));

const QuoteAuthor = styled(Typography)(({ _theme }) => ({
  fontWeight: 'bold',
}));

const CustomerPortalLink = styled(RouterLink)(({ _theme }) => ({
  color: 'var(--color-primary)',
  textDecoration: 'none',
  transition: 'all 0.3s ease',
  position: 'relative',
  '&:hover': {
    color: 'var(--color-accent)',
    '&::after': {
      width: '100%',
    }
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    left: 0,
    width: '0%',
    height: '2px',
    backgroundColor: 'var(--color-accent)',
    transition: 'width 0.3s ease',
  }
}));

const StyledBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  position: 'relative',
  width: '100%',
  maxWidth: 450,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    '& .MuiTypography-body2': {
      fontSize: '0.9rem',
    }
  }
}));

const quotes = [
  {
    "quote": "Be the change that you wish to see in the world.",
    "author": "Mahatma Gandhi"
  },
  {
    "quote": "The weak can never forgive. Forgiveness is the attribute of the strong.",
    "author": "Mahatma Gandhi"
  },
  {
    "quote": "Live as if you were to die tomorrow. Learn as if you were to live forever.",
    "author": "Mahatma Gandhi"
  },
  {
    "quote": "Happiness is when what you think, what you say, and what you do are in harmony.",
    "author": "Mahatma Gandhi"
  },
  {
    "quote": "The future depends on what we do in the present.",
    "author": "Mahatma Gandhi"
  },
  {
    "quote": "Two things are infinite: the universe and human stupidity; and I'm not sure about the universe.",
    "author": "Albert Einstein"
  },
  {
    "quote": "Imagination is more important than knowledge. Knowledge is limited. Imagination encircles the world.",
    "author": "Albert Einstein"
  },
  {
    "quote": "Life is like riding a bicycle. To keep your balance, you must keep moving.",
    "author": "Albert Einstein"
  },
  {
    "quote": "Try not to become a man of success. Rather become a man of value.",
    "author": "Albert Einstein"
  },
  {
    "quote": "The important thing is not to stop questioning. Curiosity has its own reason for existence.",
    "author": "Albert Einstein"
  },
  {
    "quote": "Success is not final, failure is not fatal: It is the courage to continue that counts.",
    "author": "Winston Churchill"
  },
  {
    "quote": "Never give in, never give in, never, never, never, never—in nothing, great or small, large or petty—never give in except to convictions of honour and good sense.",
    "author": "Winston Churchill"
  },
  {
    "quote": "If you're going through hell, keep going.",
    "author": "Winston Churchill"
  },
  {
    "quote": "The price of greatness is responsibility.",
    "author": "Winston Churchill"
  },
  {
    "quote": "We shape our buildings; thereafter they shape us.",
    "author": "Winston Churchill"
  },
  {
    "quote": "The only way to do great work is to love what you do.",
    "author": "Steve Jobs"
  },
  {
    "quote": "Your time is limited, don't waste it living someone else's life.",
    "author": "Steve Jobs"
  },
  {
    "quote": "Innovation distinguishes between a leader and a follower.",
    "author": "Steve Jobs"
  },
  {
    "quote": "Stay hungry, stay foolish.",
    "author": "Steve Jobs"
  },
  {
    "quote": "The people in the crazy ones, the misfits, the rebels, the troublemakers, the round pegs in the square holes... the ones who see things differently -- they're not fond of rules... You can quote them, disagree with them, glorify or vilify them, but the only thing you can't do is ignore them because they change things... they push the human race forward, and while some may see them as the crazy ones, we see genius, because the ones who are crazy enough to think that they can change the world, are the ones who do.",
    "author": "Steve Jobs"
  },
  {
    "quote": "The greatest glory in living lies not in never falling, but in rising every time we fall.",
    "author": "Nelson Mandela"
  },
  {
    "quote": "It always seems impossible until it's done.",
    "author": "Nelson Mandela"
  },
  {
    "quote": "Education is the most powerful weapon which you can use to change the world.",
    "author": "Nelson Mandela"
  },
  {
    "quote": "I learned that courage was not the absence of fear, but the triumph over it.",
    "author": "Nelson Mandela"
  },
  {
    "quote": "A winner is a dreamer who never gives up.",
    "author": "Nelson Mandela"
  },
  {
    "quote": "In the end, it's not the years in your life that count. It's the life in your years.",
    "author": "Abraham Lincoln"
  },
  {
    "quote": "Whatever you are, be a good one.",
    "author": "Abraham Lincoln"
  },
  {
    "quote": "The best way to predict your future is to create it.",
    "author": "Abraham Lincoln"
  },
  {
    "quote": "I am a slow walker, but I never walk back.",
    "author": "Abraham Lincoln"
  },
  {
    "quote": "Nearly all men can stand adversity, but if you want to test a man's character, give him power.",
    "author": "Abraham Lincoln"
  },
  {
    "quote": "The only impossible journey is the one you never begin.",
    "author": "Tony Robbins"
  },
  {
    "quote": "It is during our darkest moments that we must focus to see the light.",
    "author": "Aristotle"
  },
  {
    "quote": "We are what we repeatedly do. Excellence, then, is not an act, but a habit.",
    "author": "Aristotle"
  },
  {
    "quote": "Knowing yourself is the beginning of all wisdom.",
    "author": "Aristotle"
  },
  {
    "quote": "The way to get started is to quit talking and begin doing.",
    "author": "Walt Disney"
  },
  {
    "quote": "All our dreams can come true, if we have the courage to pursue them.",
    "author": "Walt Disney"
  },
  {
    "quote": "It's kind of fun to do the impossible.",
    "author": "Walt Disney"
  },
  {
    "quote": "The difference between winning and losing is most often not quitting.",
    "author": "Walt Disney"
  },
  {
    "quote": "You may not control all the events that happen to you, but you can decide not to be reduced by them.",
    "author": "Maya Angelou"
  },
  {
    "quote": "If you don't like something, change it. If you can't change it, change your attitude.",
    "author": "Maya Angelou"
  },
  {
    "quote": "I've learned that people will forget what you said, people will forget what you did, but people will never forget how you made them feel.",
    "author": "Maya Angelou"
  },
  {
    "quote": "There is no greater agony than bearing an untold story inside you.",
    "author": "Maya Angelou"
  },
  {
    "quote": "A person who never made a mistake never tried anything new.",
    "author": "Albert Einstein"
  },
  {
    "quote": "The measure of intelligence is the ability to change.",
    "author": "Albert Einstein"
  },
  {
    "quote": "Great spirits have always encountered violent opposition from mediocre minds.",
    "author": "Albert Einstein"
  },
  {
    "quote": "Logic will get you from A to Z; imagination will get you everywhere.",
    "author": "Albert Einstein"
  },
  {
    "quote": "The future belongs to those who believe in the beauty of their dreams.",
    "author": "Eleanor Roosevelt"
  },
  {
    "quote": "No one can make you feel inferior without your consent.",
    "author": "Eleanor Roosevelt"
  },
  {
    "quote": "Do one thing every day that scares you.",
    "author": "Eleanor Roosevelt"
  },
  {
    "quote": "You must do the things you think you cannot do.",
    "author": "Eleanor Roosevelt"
  },
  {
    "quote": "The only thing we have to fear is fear itself.",
    "author": "Franklin D. Roosevelt"
  },
  {
    "quote": "The only limit to our realization of tomorrow is our doubts of today.",
    "author": "Franklin D. Roosevelt"
  },
  {
    "quote": "A smooth sea never made a skilled sailor.",
    "author": "Franklin D. Roosevelt"
  },
  {
    "quote": "When you reach the end of your rope, tie a knot in it and hang on.",
    "author": "Franklin D. Roosevelt"
  },
  {
    "quote": "Believe you can and you're halfway there.",
    "author": "Theodore Roosevelt"
  },
  {
    "quote": "Do what you can, with what you have, where you are.",
    "author": "Theodore Roosevelt"
  },
  {
    "quote": "It is hard to fail, but it is worse never to have tried to succeed.",
    "author": "Theodore Roosevelt"
  },
  {
    "quote": "Keep your eyes on the stars, and your feet on the ground.",
    "author": "Theodore Roosevelt"
  },
  {
    "quote": "Far and away the best prize that life has to offer is the chance to work hard at work worth doing.",
    "author": "Theodore Roosevelt"
  },
  {
    "quote": "I have not failed. I've just found 10,000 ways that won't work.",
    "author": "Thomas Edison"
  },
  {
    "quote": "Genius is one percent inspiration and ninety-nine percent perspiration.",
    "author": "Thomas Edison"
  },
  {
    "quote": "Our greatest weakness lies in giving up. The most certain way to succeed is always to try just one more time.",
    "author": "Thomas Edison"
  },
  {
    "quote": "Opportunity is missed by most people because it is dressed in overalls and looks like work.",
    "author": "Thomas Edison"
  },
  {
    "quote": "The secret of getting ahead is getting started.",
    "author": "Mark Twain"
  },
  {
    "quote": "Kindness is the language which the deaf can hear and the blind can see.",
    "author": "Mark Twain"
  },
  {
    "quote": "The two most important days in your life are the day you are born and the day you find out why.",
    "author": "Mark Twain"
  },
  {
    "quote": "Courage is not the absence of fear, but mastery of it.",
    "author": "Mark Twain"
  },
  {
    "quote": "Whether you think you can or you think you can't, you're right.",
    "author": "Henry Ford"
  },
  {
    "quote": "Failure is simply the opportunity to begin again, this time more intelligently.",
    "author": "Henry Ford"
  },
  {
    "quote": "Coming together is a beginning; keeping together is progress; working together is success.",
    "author": "Henry Ford"
  },
  {
    "quote": "Quality means doing it right when no one is looking.",
    "author": "Henry Ford"
  },
  {
    "quote": "The best time to plant a tree was 20 years ago. The second best time is now.",
    "author": "Chinese Proverb"
  },
  {
    "quote": "A journey of a thousand miles begins with a single step.",
    "author": "Lao Tzu"
  },
  {
    "quote": "When I let go of what I am, I become what I might be.",
    "author": "Lao Tzu"
  },
  {
    "quote": "He who knows that enough is enough will always have enough.",
    "author": "Lao Tzu"
  },
  {
    "quote": "The man who moves a mountain begins by carrying away small stones.",
    "author": "Confucius"
  },
  {
    "quote": "It does not matter how slowly you go as long as you do not stop.",
    "author": "Confucius"
  },
  {
    "quote": "Our greatest glory is not in never falling, but in rising every time we fall.",
    "author": "Confucius"
  },
  {
    "quote": "Real knowledge is to know the extent of one's ignorance.",
    "author": "Confucius"
  },
  {
    "quote": "The only true wisdom is in knowing you know nothing.",
    "author": "Socrates"
  },
  {
    "quote": "An unexamined life is not worth living.",
    "author": "Socrates"
  },
  {
    "quote": "I cannot teach anybody anything. I can only make them think.",
    "author": "Socrates"
  },
  {
    "quote": "To find yourself, think for yourself.",
    "author": "Socrates"
  },
  {
    "quote": "The unexamined life is not worth living.",
    "author": "Socrates"
  },
  {
    "quote": "Yesterday is history, tomorrow is a mystery, today is a gift of God, which is why we call it the present.",
    "author": "Bill Keane"
  },
  {
    "quote": "Life is what happens to you while you're busy making other plans.",
    "author": "John Lennon"
  },
  {
    "quote": "The only way out is through.",
    "author": "Robert Frost"
  },
  {
    "quote": "Two roads diverged in a wood, and I— I took the one less traveled by, And that has made all the difference.",
    "author": "Robert Frost"
  },
  {
    "quote": "In three words I can sum up everything I've learned about life: it goes on.",
    "author": "Robert Frost"
  },
  {
    "quote": "The woods are lovely, dark and deep. But I have promises to keep, and miles to go before I sleep.",
    "author": "Robert Frost"
  },
  {
    "quote": "Don't judge each day by the harvest you reap but by the seeds that you plant.",
    "author": "Robert Louis Stevenson"
  },
  {
    "quote": "To be yourself in a world that is constantly trying to make you something else is the greatest accomplishment.",
    "author": "Ralph Waldo Emerson"
  },
  {
    "quote": "What lies behind us and what lies before us are tiny matters compared to what lies within us.",
    "author": "Ralph Waldo Emerson"
  },
  {
    "quote": "Do not go where the path may lead, go instead where there is no path and leave a trail.",
    "author": "Ralph Waldo Emerson"
  },
  {
    "quote": "The only person you are destined to become is the person you decide to be.",
    "author": "Ralph Waldo Emerson"
  },
  {
    "quote": "Write it on your heart that every day is the best day in the year.",
    "author": "Ralph Waldo Emerson"
  },
  {
    "quote": "What we plant in the soil of contemplation, we shall reap in the harvest of action.",
    "author": "Meister Eckhart"
  },
  {
    "quote": "Be yourself; everyone else is already taken.",
    "author": "Oscar Wilde"
  },
  {
    "quote": "I can resist everything except temptation.",
    "author": "Oscar Wilde"
  },
  {
    "quote": "We are all in the gutter, but some of us are looking at the stars.",
    "author": "Oscar Wilde"
  },
  {
    "quote": "Be the change you wish to see in the world.",
    "author": "Mahatma Gandhi"
  }
];

// Add loading overlay component
const LoadingOverlay = styled(motion.div)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
});

// Add this motion variant near the top of the file with other animations
const _pageTransition = {
  exit: {
    opacity: 0,
    x: -100,
    transition: {
      duration: 0.3
    }
  }
};

function Login({ onLogin }) {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [isVerificationStep, setIsVerificationStep] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [userId, setUserId] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const navigate = useNavigate();
  const [currentQuote, setCurrentQuote] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    setCurrentQuote(randomQuote);
  }, []);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!isVerificationStep) {
        const response = await loginWithVerification({ usernameOrEmail, password });
        setMaskedEmail(response.data.maskedEmail);
        setUserId(response.data.userId);
        setIsVerificationStep(true);
      } else {
        const response = await verifyCode({ userId, code: verificationCode });
        onLogin(response.data.user);
        navigate('/retail/home');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response && error.response.status === 429) {
        setCountdown(60);
      }
      setError(error.response?.data?.error || error.message || 'An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await loginWithVerification({ usernameOrEmail, password });
      setError('');
    } catch (error) {
      console.error('Error resending code:', error);
      setError('Failed to resend verification code');
    }
  };

  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
    navigate('/retail/home');
  };

  // Animation variants for framer-motion
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <Root
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ duration: 0.3 }}
    >
      <StyledPaper
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {isLoading && (
          <LoadingOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CircularProgress size={48} sx={{ color: 'var(--color-primary)' }} />
            <Typography variant="h6" sx={{ mt: 2, color: 'var(--color-primary)' }}>
              Logging in...
            </Typography>
          </LoadingOverlay>
        )}
        
        <motion.div variants={itemVariants}>
          <LogoText>PIM BY ELI</LogoText>
        </motion.div>

        <Title variant="h4">
          Welcome Back
        </Title>
        {error && <ErrorText>{error}</ErrorText>}
        <form onSubmit={handleSubmit}>
          {!isVerificationStep ? (
            <>
              <StyledTextField
                fullWidth
                label="Username or Email"
                value={usernameOrEmail}
                onChange={(e) => setUsernameOrEmail(e.target.value)}
                required
                variant="outlined"
              />
              <StyledTextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                variant="outlined"
              />
              <ButtonContainer>
                <LoginButton
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  Login
                </LoginButton>
              </ButtonContainer>
            </>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                Enter the verification code sent to {maskedEmail}
              </Typography>
              <StyledTextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                margin="normal"
              />
              {countdown > 0 ? (
                <Typography variant="body2">
                  You can request a new code in {countdown} seconds
                </Typography>
              ) : (
                <Button onClick={handleResendCode} disabled={countdown > 0}>
                  Resend Code
                </Button>
              )}
              <LoginButton
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 2 }}
              >
                Verify
              </LoginButton>
            </>
          )}
        </form>
        <QuoteBox>
          <QuoteText variant="body1">"{currentQuote.quote}"</QuoteText>
          <QuoteAuthor variant="body2">- {currentQuote.author}</QuoteAuthor>
        </QuoteBox>
        <StyledBox>
          <Typography variant="body2" color="textSecondary">
            Looking for the Customer Portal?{' '}
            <CustomerPortalLink to="/customer/login">
              Click here
            </CustomerPortalLink>
          </Typography>
        </StyledBox>
      </StyledPaper>
      <Dialog open={showSuccessDialog} onClose={handleCloseSuccessDialog}>
        <DialogTitle>Login Successful</DialogTitle>
        <DialogContent>
          <Typography>
            You have successfully logged in. Please note that a new verification system will be implemented in the next 24 hours. 
            Ensure your email address is up to date as it will be required for the new login process.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccessDialog} color="primary">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
}

export default Login;